import React from "react";
import { motion } from "framer-motion";
import { FileText, Scale, AlertTriangle, Globe, RefreshCw, Gavel } from "lucide-react";
import { Container } from "../components/layout/container";
import { useLanguage } from "../context/LanguageContext";
import SEO from "../components/SEO";

const sectionIcons = [Globe, FileText, AlertTriangle, Scale, RefreshCw, Gavel];

export default function TermsConditionPage() {
  const { t } = useLanguage();
  const termsData = t.terms;

  const sections = (termsData?.sections || []).map((sec, idx) => ({
    icon: sectionIcons[idx] || FileText,
    title: sec.title,
    content: sec.content,
  }));
  return (
    <div className="min-h-screen bg-[#0B0B0F]">
      <SEO 
        title={termsData?.seo?.title || "Terms & Conditions | Nandan Priyadarshi"}
        description={termsData?.seo?.description || "Terms and conditions for using Nandan Priyadarshi's portfolio website. Please read these terms carefully before using our services."}
        canonicalUrl="/term-condition"
        keywords={termsData?.seo?.keywords || "Terms and Conditions, Legal Terms, Nandan Priyadarshi"}
      />
      {/* Hero Section */}
      <section className="relative pt-16 pb-12 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-1/4 w-[600px] h-[600px] bg-indigo-500/5 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-slate-500/5 rounded-full blur-[100px]" />
        </div>

        <Container className="relative">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6">
              <FileText className="h-4 w-4 text-slate-400" />
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70">
                {termsData?.eyebrow || 'Legal'}
              </span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white mb-4">
              {termsData?.title || 'Terms & Conditions'}
            </h1>
            
            <p className="text-lg text-white/60 mb-2">
              {termsData?.description || 'Please read these terms carefully before using our website.'}
            </p>
            
            <p className="text-sm text-white/40">
              {termsData?.lastUpdated || 'Last updated: January 2026'}
            </p>
          </motion.div>
        </Container>
      </section>

      {/* Content Section */}
      <section className="py-12">
        <Container>
          <div className="max-w-3xl mx-auto">
            {/* Introduction */}
            <motion.div
              className="mb-12 p-6 rounded-2xl bg-white/5 border border-white/10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <p className="text-white/70 leading-relaxed">
                {termsData?.intro || `Welcome to Nandan Priyadarshi's portfolio website. These terms and conditions outline the rules and regulations for the use of this website. By accessing this website, we assume you accept these terms and conditions in full. Do not continue to use this website if you do not accept all of the terms and conditions stated on this page.`}
              </p>
            </motion.div>

            {/* Sections */}
            <div className="space-y-8">
              {sections.map((section, index) => (
                <motion.div
                  key={section.title}
                  className="group"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.15 + index * 0.05 }}
                >
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 mt-1">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/5 border border-white/10 group-hover:border-white/20 transition-colors">
                        <section.icon className="h-5 w-5 text-slate-400" />
                      </div>
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-white mb-2">
                        {section.title}
                      </h2>
                      <p className="text-sm text-white/60 leading-relaxed">
                        {section.content}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Agreement Note */}
            <motion.div
              className="mt-12 p-6 rounded-2xl bg-gradient-to-r from-white/5 to-transparent border border-white/10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <h3 className="text-sm font-semibold text-white mb-2">
                {termsData?.agreementTitle || 'Agreement to Terms'}
              </h3>
              <p className="text-sm text-white/50">
                {termsData?.agreementText || `By using this website, you signify your acceptance of these terms. If you do not agree to these terms, please do not use our website. Your continued use of the website following the posting of changes to these terms will be deemed your acceptance of those changes.`}
              </p>
            </motion.div>

            {/* Footer Note */}
            <motion.div
              className="mt-8 pt-8 border-t border-white/10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.55 }}
            >
              <p className="text-sm text-white/40 text-center">
                {(termsData?.questionsContact || 'If you have any questions about these Terms & Conditions, please contact us at')}{" "}
                <a href="mailto:nandanpriyadarshi298@gmail.com" className="text-white/60 hover:text-white transition-colors">
                  nandanpriyadarshi298@gmail.com
                </a>
              </p>
            </motion.div>
          </div>
        </Container>
      </section>
    </div>
  );
}
